/** The filter line's controls as chips: an "Add filter" button and one removable chip per active filter.
 *
 * "Add filter" opens a menu of the filters the page offers; choosing one opens its option list, with
 * the count beside each option **when the page supplied one** (`FilterOption.count`, never computed
 * here). A chosen filter becomes a chip - "Scope: 2" with a cross - and the chip reopens its list.
 * A multi-choice filter toggles any number of options; a single-choice one closes on choosing, as the
 * native selects do. "Clear all" belongs to the line and appears at two or more active filters.
 *
 * The state is the line's own `FilterState`, so a page that mirrors it in the URL (`useUrlFilters`)
 * gets a shareable address for free.
 */

import { Check, Plus, X } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { quantity } from "../../../lib/format";
import type { FilterDef, FilterOption, FilterState } from "../filters";
import { chipText } from "./chipText";
import { menuItems, onMenuKeyDown, useDismiss } from "./menuKeys";
import styles from "./tablePower.module.css";

/** An option list longer than this gets a search box: fifty sites cannot be scanned by eye. */
export const SEARCH_OPTIONS_OVER = 10;

interface Open {
  /** Which trigger the popup hangs from: "add" or a filter id. */
  owner: string;
  step: "fields" | "options";
  id?: string;
}

export function FilterChips({
  defs,
  state,
  onChange,
  request,
}: {
  defs: readonly FilterDef[];
  state: FilterState;
  onChange: (next: FilterState) => void;
  /** A column menu asked for this filter's list. A new `n` opens it again. */
  request?: { id: string; n: number } | null;
}) {
  const [open, setOpen] = useState<Open | null>(null);
  const [seen, setSeen] = useState(0);
  // What was typed into an option list's search box, and which list it was typed into: another list
  // (or the same one opened again) starts empty without an effect to reset it.
  const [found, setFound] = useState({ key: "", text: "" });
  const row = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});

  if (request && request.n !== seen) {
    setSeen(request.n);
    if (defs.some((d) => d.id === request.id)) setOpen({ owner: "add", step: "options", id: request.id });
  }

  const close = (returnFocus: boolean) => {
    const owner = open?.owner;
    setOpen(null);
    if (returnFocus && owner) triggers.current[owner]?.focus();
  };
  useDismiss(open !== null, () => setOpen(null), row);

  useEffect(() => {
    if (!open) return;
    const search = popup.current?.querySelector<HTMLElement>('input[type="search"]');
    if (search) {
      search.focus();
      return;
    }
    const items = menuItems(popup.current);
    const chosen = items.find((item) => item.getAttribute("aria-selected") === "true");
    (chosen ?? items[0])?.focus();
  }, [open]);

  const set = (id: string, next: readonly string[]) => {
    const values = { ...state.values };
    if (next.length === 0) delete values[id];
    else values[id] = next;
    onChange({ ...state, values });
  };

  const active = defs.filter((def) => (state.values[def.id] ?? []).length > 0);

  const onPopupKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === "Tab") {
      setOpen(null);
    } else if (
      event.target instanceof HTMLInputElement &&
      (event.key === "Home" || event.key === "End" || event.key === "ArrowLeft" || event.key === "ArrowRight")
    ) {
      // The search box keeps its own cursor keys.
    } else {
      onMenuKeyDown(event, popup.current);
    }
  };

  const optionsOf = (def: FilterDef) => {
    const multi = (def.mode ?? "multi") === "multi";
    const selected = state.values[def.id] ?? [];
    const choose = (option: FilterOption) => {
      if (multi) {
        set(
          def.id,
          selected.includes(option.value)
            ? selected.filter((v) => v !== option.value)
            : [...selected, option.value],
        );
      } else {
        set(def.id, selected[0] === option.value ? [] : [option.value]);
        close(true);
      }
    };
    const searchable = def.options.length > SEARCH_OPTIONS_OVER;
    const key = `${open?.owner ?? ""}:${def.id}`;
    const text = found.key === key ? found.text : "";
    const needle = text.trim().toLowerCase();
    const shown = needle ? def.options.filter((o) => o.label.toLowerCase().includes(needle)) : def.options;
    const listbox = (
      <div
        ref={searchable ? undefined : popup}
        role="listbox"
        aria-label={def.label}
        aria-multiselectable={multi}
        className={searchable ? styles.listScroll : styles.list}
        onKeyDown={searchable ? undefined : onPopupKey}
        data-testid={`filter-options-${def.id}`}
      >
        {shown.length === 0 ? <div className={styles.listEmpty}>No match</div> : null}
        {shown.map((option) => {
          const on = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={on}
              className={styles.item}
              data-value={option.value}
              onClick={() => choose(option)}
            >
              <span className={styles.itemTick} aria-hidden="true">
                {on ? <Check size={13} weight="bold" /> : null}
              </span>
              <span className={styles.itemLabel}>{option.label}</span>
              {option.count !== undefined ? (
                <span className={styles.itemHint} data-testid="option-count">
                  {quantity(String(option.count))}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
    if (!searchable) return listbox;
    return (
      <div ref={popup} className={styles.list} onKeyDown={onPopupKey} data-testid={`filter-panel-${def.id}`}>
        <input
          type="search"
          className={styles.listSearch}
          aria-label={`Find in ${def.label}`}
          placeholder={`Find ${def.label.toLowerCase()}…`}
          value={text}
          onChange={(event) => setFound({ key, text: event.target.value })}
          data-testid={`filter-find-${def.id}`}
        />
        {listbox}
      </div>
    );
  };

  const popupFor = (owner: string) => {
    if (open?.owner !== owner) return null;
    if (open.step === "options") {
      const def = defs.find((d) => d.id === open.id);
      return def ? optionsOf(def) : null;
    }
    return (
      <div
        ref={popup}
        role="menu"
        aria-label="Add filter"
        className={styles.list}
        onKeyDown={onPopupKey}
        data-testid="filter-fields"
      >
        {defs.map((def) => (
          <button
            key={def.id}
            type="button"
            role="menuitem"
            className={styles.item}
            data-testid={`add-filter-${def.id}`}
            onClick={() => setOpen({ owner, step: "options", id: def.id })}
          >
            <span className={styles.itemLabel}>{def.label}</span>
            {(state.values[def.id] ?? []).length > 0 ? (
              <span className={styles.itemHint}>{(state.values[def.id] ?? []).length}</span>
            ) : null}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div ref={row} className={styles.chips} data-testid="filter-chips">
      <span className={styles.pop}>
        <button
          ref={(node) => {
            triggers.current.add = node;
          }}
          type="button"
          className={styles.addFilter}
          aria-haspopup="menu"
          aria-expanded={open?.owner === "add"}
          data-testid="add-filter"
          disabled={defs.length === 0}
          onClick={() => (open?.owner === "add" ? close(false) : setOpen({ owner: "add", step: "fields" }))}
        >
          <Plus size={13} weight="bold" aria-hidden="true" />
          Add filter
        </button>
        {popupFor("add")}
      </span>
      {active.map((def) => {
        const values = state.values[def.id] ?? [];
        return (
          <span key={def.id} className={styles.pop}>
            <span className={styles.chipOn} data-testid={`chip-${def.id}`}>
              <button
                ref={(node) => {
                  triggers.current[def.id] = node;
                }}
                type="button"
                className={styles.chipMain}
                aria-haspopup="listbox"
                aria-expanded={open?.owner === def.id}
                onClick={() =>
                  open?.owner === def.id ? close(false) : setOpen({ owner: def.id, step: "options", id: def.id })
                }
              >
                {chipText(def, values)}
              </button>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove filter ${def.label}`}
                data-testid={`chip-remove-${def.id}`}
                onClick={() => set(def.id, [])}
              >
                <X size={11} weight="bold" aria-hidden="true" />
              </button>
            </span>
            {popupFor(def.id)}
          </span>
        );
      })}
    </div>
  );
}
